import { HeroUIPluginConfig, heroui } from '@heroui/theme';

const bndkLightColors = {
  default: {
    '50': '#F9FAFB',
    '100': '#F2F4F7',
    '200': '#EAECF0',
    '300': '#D0D5DD',
    '400': '#98A2B3',
    '500': '#667085',
    '600': '#475467',
    '700': '#344054',
    '800': '#182230',
    '900': '#101828',
    foreground: '#000',
    DEFAULT: '#D0D5DD',
  },
  primary: {
    '50': '#fef2f3',
    '100': '#ffe1e4',
    '200': '#ffc9cf',
    '300': '#fea3ad',
    '400': '#fb6e7e',
    '500': '#f34054',
    '600': '#db1e33',
    '700': '#bd182a',
    '800': '#9c1827',
    '900': '#811b26',
    foreground: '#FFFF',
    DEFAULT: '#db1e33',
  } as Record<string, string>,
  secondary: {
    '50': '#fbf9f1',
    '100': '#f6f1de',
    '200': '#ecdfbc',
    '300': '#dfc892',
    '400': '#d1ab66',
    '500': '#c79548',
    '600': '#b97f3d',
    '700': '#9a6634',
    '800': '#654429',
    '900': '#362214',
    foreground: '#000',
    DEFAULT: '#FFFF',
  },
  success: {
    '50': '#f3faf5',
    '100': '#def3e3',
    '200': '#bfe8ca',
    '300': '#99dcae',
    '400': '#6ccf8f',
    '500': '#3fc271',
    '600': '#35a863',
    '700': '#2b8d54',
    '800': '#217346',
    '900': '#195c39',
    foreground: '#fff',
    DEFAULT: '#3fc271',
  },
  warning: {
    '50': '#fff9eb',
    '100': '#fff2cf',
    '200': '#ffeab3',
    '300': '#ffe297',
    '400': '#ffda7b',
    '500': '#ffd25f',
    '600': '#d2ad4e',
    '700': '#a6893e',
    '800': '#79642d',
    '900': '#4d3f1d',
    foreground: '#000',
    DEFAULT: '#ffd25f',
  },
  danger: {
    '50': '#fff1f2',
    '100': '#ffe4e6',
    '200': '#fecdd3',
    '300': '#fda4af',
    '400': '#fb7185',
    '500': '#f43f5e',
    '600': '#e11d48',
    '700': '#be123c',
    '800': '#9f1239',
    '900': '#881337',
    foreground: '#fff',
    DEFAULT: '#f43f5e',
  },
  background: '#fffdfc',
  foreground: {
    '50': '#f3f4f6',
    '100': '#f3f4f6',
    '200': '#474f5d',
    '300': '#566173',
    '400': '#6c798b',
    '500': '#8a96a6',
    '600': '#b3bbc6',
    '700': '#d7dbe0',
    '800': '#edeef1',
    '900': '#f3f4f6',
    DEFAULT: '#0C111D',
    foreground: '#f3f4f6',
  },
  content1: { DEFAULT: '#FFFF', foreground: '#000' },
  content2: { DEFAULT: '#edeef1', foreground: '#000' },
  content3: { DEFAULT: '#FFF', foreground: '#f3f4f6' },
  content4: { DEFAULT: '#FFFF', foreground: '#000' },
  focus: '#D3232D',
  overlay: { DEFAULT: '#000000' },
  divider: '#111111',
} as const;

const bndkLayout = {
  fontSize: { tiny: '0.75rem', small: '0.875rem', medium: '1rem', large: '1.125rem' },
  lineHeight: { tiny: '1rem', small: '1.25rem', medium: '1.5rem', large: '1.75rem' },
  radius: { small: '25px', medium: '25px', large: '25px' },
  borderWidth: { small: '1px', medium: '2px', large: '3px' },
  disabledOpacity: '0.5',
  dividerWeight: '1',
  hoverOpacity: '0.9',
};

const mtbLightColors = {
  default: {
    '50': '#f6f5f4',
    '100': '#e9e7e5',
    '200': '#dbd9d6',
    '300': '#cecbc6',
    '400': '#c1bdb7',
    '500': '#b4afa8',
    '600': '#95908b',
    '700': '#75726d',
    '800': '#565350',
    '900': '#363532',
    foreground: '#000',
    DEFAULT: '#d4d4d8',
  },
  primary: {
    '50': '#fef2f3',
    '100': '#fee2e4',
    '200': '#fca5aa',
    '300': '#f7727a',
    '400': '#f7727a',
    '500': '#ee454f',
    '600': '#d3232d',
    '700': '#b81d26',
    '800': '#981c23',
    '900': '#7f1d23',
    foreground: '#FFFF',
    DEFAULT: '#D3232D',
  },
  secondary: {
    '50': '#eaf0f0',
    '100': '#cedadb',
    '200': '#b1c5c6',
    '300': '#94afb0',
    '400': '#779a9b',
    '500': '#5a8486',
    '600': '#4a6d6f',
    '700': '#3b5657',
    '800': '#2b3f40',
    '900': '#1b2828',
    foreground: '#FFFF',
    DEFAULT: '#000',
  },
  success: {
    '50': '#f3faf5',
    '100': '#def3e3',
    '200': '#bfe8ca',
    '300': '#99dcae',
    '400': '#6ccf8f',
    '500': '#3fc271',
    '600': '#35a863',
    '700': '#2b8d54',
    '800': '#217346',
    '900': '#195c39',
    foreground: '#fff',
    DEFAULT: '#3fc271',
  },
  warning: {
    '50': '#fff9eb',
    '100': '#fff2cf',
    '200': '#ffeab3',
    '300': '#ffe297',
    '400': '#ffda7b',
    '500': '#ffd25f',
    '600': '#d2ad4e',
    '700': '#a6893e',
    '800': '#79642d',
    '900': '#4d3f1d',
    foreground: '#000',
    DEFAULT: '#ffd25f',
  },
  danger: {
    '50': '#fff1f2',
    '100': '#ffe4e6',
    '200': '#fecdd3',
    '300': '#fda4af',
    '400': '#fb7185',
    '500': '#f43f5e',
    '600': '#e11d48',
    '700': '#be123c',
    '800': '#9f1239',
    '900': '#881337',
    foreground: '#fff',
    DEFAULT: '#f43f5e',
  },
  background: '#fffdfc',
  foreground: {
    '50': '#f3f4f6',
    '100': '#edeef1',
    '200': '#d7dbe0',
    '300': '#b3bbc6',
    '400': '#8a96a6',
    '500': '#6c798b',
    '600': '#566173',
    '700': '#474f5d',
    '800': '#3d444f',
    '900': '#363b44',
    foreground: '#f3f4f6',
    DEFAULT: '#0C111D',
  },
  content1: { DEFAULT: '#FFFF', foreground: '#000' },
  content2: { DEFAULT: '#edeef1', foreground: '#000' },
  content3: { DEFAULT: '#e4e4e7', foreground: '#000' },
  content4: { DEFAULT: '#F3F4F6', foreground: '#000' },
  focus: '#D3232D',
  overlay: { DEFAULT: '#000000' },
  divider: '#FFFFFF',
} as const;

const mtbLayout = {
  fontSize: { tiny: '0.75rem', small: '0.875rem', medium: '1rem', large: '1.125rem' },
  lineHeight: { tiny: '1rem', small: '1.25rem', medium: '1.5rem', large: '1.75rem' },
  radius: { small: '0.5rem', medium: '0.75rem', large: '0.875rem' },
  borderWidth: { small: '1px', medium: '2px', large: '3px' },
  disabledOpacity: '0.5',
  dividerWeight: '1',
  hoverOpacity: '0.9',
};

const ameatColors = {
  default: {
    '50': '#f6f5f4',
    '100': '#e7e4e0',
    '200': '#cfcac3',
    '300': '#afa89f',
    '400': '#8a8076',
    '500': '#6b625a',
    '600': '#524a43',
    '700': '#3d362f',
    '800': '#2a2520',
    '900': '#1c1814',
    foreground: '#181513',
    DEFAULT: '#e7e4e0',
  },
  primary: {
    '50': '#fdf3f4',
    '100': '#fbe1e3',
    '200': '#f6c2c7',
    '300': '#ef98a0',
    '400': '#e35a67',
    '500': '#d22236',
    '600': '#b81c2e',
    '700': '#991826',
    '800': '#7d1722',
    '900': '#67181f',
    foreground: '#FFFFFF',
    DEFAULT: '#D22236',
  } as Record<string, string>,
  secondary: {
    '50': '#fdfbf6',
    '100': '#F5EEDC',
    '200': '#ecdfc3',
    '300': '#e0cda1',
    '400': '#d2b67a',
    '500': '#c4a05a',
    '600': '#a9854a',
    '700': '#87683b',
    '800': '#5f4a2c',
    '900': '#382c1c',
    foreground: '#181513',
    DEFAULT: '#F5EEDC',
  },
  success: {
    '50': '#f3faf5',
    '100': '#def3e3',
    '200': '#bfe8ca',
    '300': '#99dcae',
    '400': '#6ccf8f',
    '500': '#3fc271',
    '600': '#35a863',
    '700': '#2b8d54',
    '800': '#217346',
    '900': '#195c39',
    foreground: '#fff',
    DEFAULT: '#3fc271',
  },
  warning: {
    '50': '#fff9eb',
    '100': '#fff2cf',
    '200': '#ffeab3',
    '300': '#ffe297',
    '400': '#ffda7b',
    '500': '#ffd25f',
    '600': '#d2ad4e',
    '700': '#a6893e',
    '800': '#79642d',
    '900': '#4d3f1d',
    foreground: '#000',
    DEFAULT: '#ffd25f',
  },
  danger: {
    '50': '#fff1f2',
    '100': '#ffe4e6',
    '200': '#fecdd3',
    '300': '#fda4af',
    '400': '#fb7185',
    '500': '#f43f5e',
    '600': '#e11d48',
    '700': '#be123c',
    '800': '#9f1239',
    '900': '#881337',
    foreground: '#fff',
    DEFAULT: '#f43f5e',
  },
  background: '#FDFBF6',
  foreground: {
    '50': '#f5eddc',
    '100': '#ece4d4',
    '200': '#d8cfbf',
    '300': '#bdb3a3',
    '400': '#9c9183',
    '500': '#7a6f63',
    '600': '#564d43',
    '700': '#3d362f',
    '800': '#2a2520',
    '900': '#181513',
    foreground: '#F5EDDC',
    DEFAULT: '#181513',
  },
  content1: { DEFAULT: '#FFFFFF', foreground: '#181513' },
  content2: { DEFAULT: '#F5EDDC', foreground: '#181513' },
  content3: { DEFAULT: '#ece4d4', foreground: '#181513' },
  content4: { DEFAULT: '#d8cfbf', foreground: '#181513' },
  focus: '#D22236',
  overlay: { DEFAULT: '#000000' },
  divider: '#d8cfbf',
} as const;

const ameatLayout = {
  fontSize: { tiny: '0.75rem', small: '0.875rem', medium: '1rem', large: '1.125rem' },
  lineHeight: { tiny: '1rem', small: '1.25rem', medium: '1.5rem', large: '1.75rem' },
  radius: { small: '0.5rem', medium: '0.75rem', large: '0.875rem' },
  borderWidth: { small: '1px', medium: '2px', large: '3px' },
  disabledOpacity: '0.5',
  dividerWeight: '1',
  hoverOpacity: '0.9',
};

export const themeConfigs = {
  bndk: { colors: bndkLightColors, layout: bndkLayout },
  mtb: { colors: mtbLightColors, layout: mtbLayout },
  ameat: { colors: ameatColors, layout: ameatLayout },
} as const;

export type ThemeName = keyof typeof themeConfigs;

export const THEME_NAMES: ThemeName[] = ['bndk', 'mtb', 'ameat'];

export const heroUiTheme: HeroUIPluginConfig = {
  themes: {
    studio: {
      extend: 'light',
      colors: {
        primary: {
          '50': '#eff6ff',
          '100': '#dbeafe',
          '200': '#bfdbfe',
          '300': '#93c5fd',
          '400': '#60a5fa',
          '500': '#3b82f6',
          '600': '#2563eb',
          '700': '#1d4ed8',
          '800': '#1e40af',
          '900': '#1e3a8a',
          foreground: '#FFFF',
          DEFAULT: '#3b82f6',
        },
        focus: '#3b82f6',
      },
    },
    bndk: {
      colors: bndkLightColors as Record<string, unknown>,
      layout: bndkLayout,
    },
    mtb: {
      colors: mtbLightColors as Record<string, unknown>,
      layout: mtbLayout,
    },
    ameat: {
      colors: ameatColors as Record<string, unknown>,
      layout: ameatLayout,
    },
  },
};

// Disable console.log for heroui theme because custom properties such as gradient
// eslint-disable-next-line no-console
const originalConsoleLog = console.log;
// eslint-disable-next-line no-console
console.log = () => {};
export default heroui(heroUiTheme);
// eslint-disable-next-line no-console
console.log = originalConsoleLog;
